import { ref, Ref } from 'vue'
import { debounce } from 'lodash'
import apiOpenUseCase from '@/usecase/apiOpenUseCase'

export interface IEmployeeListItem {
  id: string
  fullName: string
  personnelNumber: string
  oldPersonnelNumber?: string | null
  phoneNumber?: string | null
  email?: string | null
  imageUrl?: string | null
  authUserId?: string | null
  nfcCardIdentifier?: string | null
  joinDate?: string | null
  latestEmployment?: {
    id?: string
    positionId?: string
    positionName?: string
    employmentTypeId?: string
    employmentTypeName?: string
    regionId?: string
    regionName?: string
    startDate?: string
    endDate?: string | null
    isActive?: boolean
  } | null
  createdAt?: string
  updatedAt?: string
}

export function useCrewList(appliedPositionIds: Ref<string[]>) {
  const employees = ref<IEmployeeListItem[]>([])
  const totalRecords = ref(0)
  const pageNumber = ref(1)
  const pageSize = ref(10)
  const searchQuery = ref('')
  const isLoading = ref(false)

  const buildQueryString = () => {
    const params: string[] = [
      `pageNumber=${pageNumber.value}`,
      `pageSize=${pageSize.value}`
    ]

    if (searchQuery.value.trim()) {
      params.push(`fullName=${encodeURIComponent(searchQuery.value.trim())}`)
    }

    if (appliedPositionIds.value.length > 0) {
      appliedPositionIds.value.forEach((id) => {
        params.push(`LatestEmployment.PositionIds=${encodeURIComponent(id)}`)
      })
    }

    return `?${params.join('&')}`
  }

  const fetchEmployees = async (resetPage = false) => {
    if (resetPage) {
      pageNumber.value = 1
    }
    isLoading.value = true
    try {
      const queryStr = buildQueryString()
      const response = await apiOpenUseCase.get('/talent/v1/employees', queryStr)
      const data = response?.result?.data ?? response?.result ?? []
      employees.value = data
      totalRecords.value = response?.result?.count ?? response?.count ?? 0
    } catch (err) {
      console.error('Error fetching employees:', err)
      employees.value = []
      totalRecords.value = 0
    } finally {
      isLoading.value = false
    }
  }

  const debouncedSearch = debounce(() => {
    fetchEmployees(true)
  }, 400)

  const handleSearch = (query: string) => {
    searchQuery.value = query
    debouncedSearch()
  }

  const onPageChange = (event: { page: number; rows: number }) => {
    pageNumber.value = event.page + 1
    pageSize.value = event.rows
    fetchEmployees(false)
  }

  return {
    employees,
    totalRecords,
    pageNumber,
    pageSize,
    searchQuery,
    isLoading,
    fetchEmployees,
    handleSearch,
    onPageChange
  }
}
